import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Container, Image, Modal } from 'react-bootstrap';
import { fetchUsers } from '../services/UserService';
import { fetchActivitiesForUser } from '../services/ActivityService';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import ActivitiesFeed from '../components/ActivitiesFeed/ActivitiesFeed';

const UserProfile = () => {
    const { username } = useParams();
    const [profile, setProfile] = useState(null);
    const [activitiesFeed, setActivitiesFeed] = useState([]);
    const [nextPageToken, setNextPageToken] = useState(undefined);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const activityIds = useRef(new Set());
    const initialFetchDone = useRef(false);
    const [enlargedImage, setEnlargedImage] = useState(null);

    const processActivities = (activities) => {
        return activities.map(activity => ({
            ...activity,
            isPersonalBest: false, // Calculate based on actual data
            isPersonalWorst: false, // Calculate based on actual data
        }));
    };

    const loadMoreActivities = useCallback(async () => {
        if (loading || !initialFetchDone.current || error || !hasMore) return;

        try {
            setLoading(true)
            const response = await fetchActivitiesForUser(username, undefined, undefined, nextPageToken, 10);
            const fetchedActivities = response.data


            setHasMore(!response.isLast);
            setNextPageToken(response.lastEvaluatedKey)

            const newActivities = fetchedActivities.filter(activity => !activityIds.current.has(activity.created_at));
            newActivities.forEach(activity => activityIds.current.add(activity.created_at));
            if (newActivities.length === 0) return;

            const allActivities = processActivities(newActivities);


            setActivitiesFeed(prevActivities => {
                const updatedActivities = [...prevActivities, ...allActivities];
                return updatedActivities;
            });

        } catch (error) {
            console.error("Failed to fetch activities (loadMore)", error);
        } finally {
            setLoading(false)
        }
    }, [username, loading, error, nextPageToken, hasMore]);

    const fetchProfile = useCallback(async () => {
        if (!username) return;
        setError(null);

        try {
            const response = await fetchUsers(username);
            const userData = response.users[0]; // Get the first element, it should never contain more than one. 
            setProfile(userData);
        } catch (err) {
            console.error('Fetch error:', err);
            setError('Failed to fetch data. Please try again later.');
        }
    }, [username]);

    useEffect(() => {
        if (initialFetchDone.current || loading || activitiesFeed.length > 0) return;
        initialFetchDone.current = true;
        setLoading(true)


        const fetchInitialActivities = async () => {
            setLoading(true);
            setError(null);
            activityIds.current.clear();
            setHasMore(true);

            try {
                const response = await fetchActivitiesForUser(username, undefined, undefined, nextPageToken, 10);
                const fetchedActivities = response.data

                const newActivities = fetchedActivities.filter(activity => !activityIds.current.has(activity.created_at));
                newActivities.forEach(activity => activityIds.current.add(activity.created_at));

                const allActivities = processActivities(newActivities);

                setActivitiesFeed(allActivities);

                setHasMore(!response.isLast);
                setNextPageToken(response.lastEvaluatedKey);
            } catch (error) {
                console.error("Failed to fetch activities (initial)", error);
                setError('Failed to fetch activities.');
            } finally {
                setLoading(false);
            }
        };

        fetchProfile();
        fetchInitialActivities();
    }, [username, loading, activitiesFeed, fetchProfile, nextPageToken]);

    const handleImageClick = useCallback((src) => {
        setEnlargedImage(src);
    }, []);

    const handleClose = useCallback(() => {
        setEnlargedImage(null);
    }, []);

    useInfiniteScroll(loadMoreActivities);

    if (error) return <Container>Error: {error}</Container>;

    return (
        <Container>
            <Card className="mb-3">
                <Card.Header className="d-flex align-items-center" >
                    {profile?.profilePicture && (
                        <Image
                            src={profile?.profilePicture}
                            roundedCircle
                            style={{ width: 40, height: 40, marginRight: 10 }}
                            alt="profile"
                        />
                    )}
                    {<div>{profile?.firstName}'s Profile</div>}
                </Card.Header>
                <Card.Body>
                    <Card.Text>{profile?.email}</Card.Text>
                </Card.Body>
            </Card>
            <ActivitiesFeed activities={activitiesFeed} onImageClick={handleImageClick} />
            <div id="scroll-anchor" style={{ height: 1 }}></div> { }
            <Modal show={!!enlargedImage} onHide={handleClose} centered>
                <Modal.Body className="p-0 d-flex justify-content-center align-items-center">
                    {enlargedImage && <Image src={enlargedImage} alt="Enlarged activity" className="modal-image" />}
                </Modal.Body>
            </Modal>
        </Container>
    );
};

export default UserProfile;
